
import { Button, Form, RadioChangeEvent, Select } from 'antd';
import '../../DashboardWithdraw.less';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { forEach } from 'lodash';
import useSpinner from '../../../../hooks/layout/useSpinner';
import useNotification from '../../../../hooks/layout/useNotification';
import { GET_BANK_ACCOUNTS } from '../../../bank-accounts/BankAccount.gql';
import { SUBMIT_WITHDRAWAL } from '../../DashboardWithdraw.gql';
import { GET_USER_PROFILE } from '../../../login/Login.gql';
import { SET_CURRENT_USER, useAuth } from '../../../../hooks/auth/AuthContext';
import RadioGroups from '../../../../shared/components/radio-groups/RadioGroups';
import DashboardInputs from '../../../../shared/components/dashboard-str-input/DashboardInput';
import { ExclamationCircleOutlined } from '@ant-design/icons';

const DashboardWithdrawForm = () => {
    const [form] = Form.useForm();
    const { setLoading } = useSpinner();
    const { setSuccessNotification, setErrorNotification } = useNotification();
    const intl = useIntl();
    const [selectedWithdrawOption, setSelectedWithdrawOption] = useState('banktrf');
    const { data: bankAccountsData, loading: bankAccountsLoading } = useQuery(GET_BANK_ACCOUNTS,
        { fetchPolicy: "no-cache" });
    const [submitWithdrawal, { data: submitWithdrawalData, loading: submitWithdrawalLoading }]
        = useMutation(SUBMIT_WITHDRAWAL,
            { errorPolicy: "all" });
    const [bankAccountDetailList, setBankAccountDetailList] = useState([]);
    const [bankAccountList, setBankAccountList] = useState([]);
    const [selectedBankAccountList, setselectedBankAccountList] = useState([]);
    const [bankAccountName, setBankAccountName] = useState('');
    const [bankAccountNumber, setBankAccountNumber] = useState('');
    const [selectedBankAccId, setSelectedBankAccId] = useState('');
    const [restOfFormVisible, setRestOfFormVisible] = useState(false);
    const [getUserProfile, { data, error }] = useLazyQuery(GET_USER_PROFILE, {
        fetchPolicy: 'no-cache',
        errorPolicy: "all",
        //notifyOnNetworkStatusChange: true
    });
    const { authDispatch } = useAuth();
    const [withdrawOptions, setWithdrawOptions] = useState<any>();
    const [accountLabelName, setAccountLabelName] = useState('DASHBOARD_WITHDRAW_BANK');

    useEffect(() => {
        if (bankAccountsLoading || submitWithdrawalLoading)
            setLoading(true);
        else
            setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bankAccountsLoading, submitWithdrawalLoading])

    useEffect(() => {
        if (submitWithdrawalData && submitWithdrawalData.createWithdraw && submitWithdrawalData.createWithdraw.status) {
            if (submitWithdrawalData.createWithdraw.status === 'SUCCESS') {
                setSuccessNotification(intl.formatMessage({ id: 'DASHBOARD_WITHDRAW_SUCCESSFUL' }));
                getUserProfile();
            } else {
                if (submitWithdrawalData.createWithdraw.status === 'INVALID_WITHDRAWAL_REQUEST') {
                    setErrorNotification(intl.formatMessage({ id: `DASHBOARD_WITHDRAW_FAIL` }));
                } else {
                    setErrorNotification(intl.formatMessage({ id: `ERROR_${submitWithdrawalData.createWithdraw.status}` }));
                }
            }
        }// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitWithdrawalData])

    useEffect(() => {
        if (data && !error) {
            authDispatch({ type: SET_CURRENT_USER, payload: data });
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, error]);

    useEffect(() => {
        if (bankAccountsData && bankAccountsData.bankAccounts && bankAccountsData.bankAccounts.bankAccounts) {
            let accList: any = [];
            let _withdrawOption: any = [];
            forEach(bankAccountsData.bankAccounts.bankAccounts, (bankAccount) => {
                accList.push({
                    icon: bankAccount.bank ? bankAccount.bank.imageUrl : "",
                    value: bankAccount.id,
                    name: bankAccount.bank ? bankAccount.bank.name : bankAccount.accountName ?? "",
                    bankId: bankAccount.bank ? bankAccount.bank.id : "",
                    withdrawMethodId: bankAccount.withdrawMethodId
                });

                _withdrawOption.push({
                    icon: bankAccount?.withdrawMethod?.iconUrl,
                    value: bankAccount?.withdrawMethodId
                })
            });

            setWithdrawOptions(_withdrawOption);
            setBankAccountDetailList(bankAccountsData.bankAccounts.bankAccounts);
            setBankAccountList(accList);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bankAccountsData])

    let withdrawAccNameNumRules = (type: string) => {
        return [
            {
                required: true,
                message: <FormattedMessage id={type === 'name' ? 'DASHBOARD_WITHDRAW_ACC_NAME_REQUIRED' : 'DASHBOARD_WITHDRAW_ACC_NUM_REQUIRED'} />,
            },
        ]
    };

    let withdrawAmtRules: any = [
        {
            required: true,
            message: <FormattedMessage id={'DASHBOARD_WITHDRAW_AMOUNT_REQUIRED'} />,
        },

        {
            pattern: /^\d+$/,
            message: <FormattedMessage id="FORM_INPUT_NUMBER_ONLY" />,
        },
        {
            type: 'number', min: 50, message: <FormattedMessage id="DASHBOARD_WITHDRAW_AMOUNT_MINMAX" values={{ 0: 'Minimum', 1: 50 }} />
        },
        {
            type: 'number', max: 50000, message: <FormattedMessage id="DASHBOARD_WITHDRAW_AMOUNT_MINMAX" values={{ 0: 'Maximum', 1: 50000 }} />
        },
    ];

    const changeAccountNameLabel = (withdrawOption: any) => {
        switch (withdrawOption) {
            case 'cl9y8jg15000009jpad9n7gv0':
                return 'DASHBOARD_WITHDRAW_BANK';
            default:
                return 'DASHBOARD_WITHDRAW_ACC'
        }
    }

    const onWithdrawOptionChange = (e: RadioChangeEvent) => {
        setSelectedWithdrawOption(e.target.value);
        setAccountLabelName(changeAccountNameLabel(e.target.value));

        //reset field
        setRestOfFormVisible(false);
        form.setFieldsValue({ "withdraw_bank_acc": null })

        let _localBankAccount: any = [];

        if (bankAccountList && bankAccountList.length > 0) {
            forEach(bankAccountList, (bankAccount: any) => {
                if (bankAccount.withdrawMethodId === e.target.value) {
                    _localBankAccount.push(bankAccount);
                }
            });

            setselectedBankAccountList(_localBankAccount)
        }
    };

    const onBankAccSelectChange = (value: string) => {
        let selectedBankAcc: any = bankAccountDetailList.filter((x: any) => x.id === value);

        if (selectedBankAcc) {
            setBankAccountName(selectedBankAcc[0].accountName);
            setBankAccountNumber(selectedBankAcc[0].accountNumber);
            setSelectedBankAccId(value);
            setRestOfFormVisible(true);

            form.setFieldsValue({
                withdraw_acc: selectedBankAcc[0].accountName,
                withdraw_acc_num: selectedBankAcc[0].accountNumber,
            });
        }
    }

    const onFinish = (fieldsValue: any) => {
        const variables: any = {
            createWithdrawInput: {
                bankAccountId: selectedBankAccId,
                amount: fieldsValue.withdraw_amount
            },
        };
        submitWithdrawal({ variables });
    };

    return (
        <>
            <div className="dashboard-withdraw-form-container">
                <Form
                    name="basic"
                    form={form}
                    autoComplete="off"
                    onFinish={onFinish}
                    initialValues={{ withdraw_option: selectedWithdrawOption }}>
                    <RadioGroups onChange={onWithdrawOptionChange} labelName={'DASHBOARD_WITHDRAW_OPTIONS'} radioOptionList={withdrawOptions} formName={'withdraw_option'} isImage={true} radioClassName={'imgRadio'} />
                    <div className='withdraw-form-items'>
                        <div className='withdraw-form-item-label'><FormattedMessage id={accountLabelName} /></div>
                        <Form.Item
                            className='withdraw-form-dropdown'
                            name="withdraw_bank_acc">
                            <Select
                                className="withdraw-dropdown"
                                onChange={onBankAccSelectChange}
                                dropdownClassName="withdraw-wallet-dropdown-list"
                                placeholder={intl.formatMessage({ id: 'DASHBOARD_WITHDRAW_BANK_ACCOUNT_PLACEHOLDER' })}
                            >
                                {selectedBankAccountList?.map((val: any) => {
                                    return (
                                        <Select.Option key={val.value} value={val.value}>
                                            {val.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        </Form.Item>
                    </div>
                    {restOfFormVisible &&
                        <>
                            <DashboardInputs rules={withdrawAccNameNumRules('name')} inputType='string' label='DASHBOARD_WITHDRAW_ACC_NAME' formItemName='withdraw_acc' inputPlaceholder={''} disabled={bankAccountName ? true : false} />
                            <DashboardInputs rules={withdrawAccNameNumRules('num')} inputType='string' label='DASHBOARD_WITHDRAW_ACC_NO' formItemName='withdraw_acc_num' inputPlaceholder={''} disabled={bankAccountNumber ? true : false} />
                            <DashboardInputs rules={withdrawAmtRules} inputType='number' label='DASHBOARD_WITHDRAW_AMT' formItemName='withdraw_amount' inputPlaceholder={intl.formatMessage({ id: "DASHBOARD_WITHDRAW_MIN_MAX" }, {
                                0: intl.formatMessage({ id: "CURRENCY_SYMBOL_BDT" }) + '50',
                                1: intl.formatMessage({ id: "CURRENCY_SYMBOL_BDT" }) + '50000',
                            })} />
                            <div className='withdraw-form-items'>
                                <div className='withdraw-form-item-label'></div>
                                <div className='withdraw-amt-info-warning'>
                                    <ExclamationCircleOutlined />
                                    <div className='withdraw-amt-info-warning-txt'>
                                        <FormattedMessage id='DASHBOARD_WITHDRAW_DAILY_LIMIT' values={{
                                            0: intl.formatMessage({ id: "CURRENCY_SYMBOL_BDT" }) + '50',
                                            1: intl.formatMessage({ id: "CURRENCY_SYMBOL_BDT" }) + '50000',
                                        }} />
                                    </div>
                                </div>
                            </div>
                            <div className='withdraw-form-items-btn'>
                                <div className='withdraw-form-item-label'>
                                </div>
                                <Form.Item>
                                    <Button className='primary-btn withdraw-submit-btn' onClick={() => { }} htmlType='submit'><FormattedMessage id='FORM_SUBMIT_BTN'/></Button>
                                </Form.Item>
                            </div>
                        </>
                    }
                </Form>
            </div>
        </>
    )
}

export default DashboardWithdrawForm;